import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Loader2, AlertCircle, Package, Building2, ShoppingCart, RotateCcw,
  ArrowRightLeft, FileText, ExternalLink, BadgeDollarSign
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  in_stock: 'bg-green-100 text-green-700',
  sold: 'bg-red-100 text-red-700',
  returned_to_vendor: 'bg-rose-100 text-rose-700',
  assigned_to_dc: 'bg-amber-100 text-amber-700',
  rented: 'bg-purple-100 text-purple-700',
  servicing: 'bg-yellow-100 text-yellow-700',
};

const TYPE_META = {
  purchase: { icon: Package,        ring: 'ring-blue-200',   dot: 'bg-blue-500',   label: 'Purchase' },
  transfer: { icon: ArrowRightLeft, ring: 'ring-purple-200', dot: 'bg-purple-500', label: 'Transfer' },
  dc:       { icon: FileText,       ring: 'ring-amber-200',  dot: 'bg-amber-500',  label: 'Delivery Challan' },
  sale:     { icon: ShoppingCart,   ring: 'ring-green-200',  dot: 'bg-green-500',  label: 'Sale' },
  sale_it:  { icon: ShoppingCart,   ring: 'ring-emerald-200',dot: 'bg-emerald-500',label: 'Internal Sale' },
  return:   { icon: RotateCcw,      ring: 'ring-rose-200',   dot: 'bg-rose-500',   label: 'Return' },
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const formatINR  = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '-';

export const ItemDetail = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/items/${itemId}/details`, getAuthHeaders())
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.detail || 'Failed to load item details'))
      .finally(() => setLoading(false));
  }, [itemId]);

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  if (error || !data) return (
    <div data-testid="item-detail-error">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"><ArrowLeft className="h-4 w-4" /> Back</button>
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm flex items-center gap-2"><AlertCircle className="h-5 w-5" />{error || 'Not found'}</div>
    </div>
  );

  const { item_id, status, batch_number, sub_rack_name, product, current_company, current_inward, linked, timeline } = data;

  return (
    <div data-testid="item-detail-page">
      <button onClick={() => navigate(-1)} data-testid="back-btn"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Item ID</p>
          <h1 className="text-3xl font-bold font-mono text-slate-900">{item_id}</h1>
          {product && (
            <p className="text-slate-600 mt-1 text-sm">
              {product.name}{product.product_code ? <span className="text-slate-400 font-mono ml-2">{product.product_code}</span> : null}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center px-3 py-1.5 text-xs font-bold uppercase rounded-sm ${STATUS_STYLES[status] || 'bg-slate-100 text-slate-700'}`}>
            {(status || 'unknown').replace(/_/g, ' ')}
          </span>
          {current_company && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase rounded-sm bg-blue-100 text-blue-700">
              <Building2 className="h-3.5 w-3.5" /> {current_company.name}
            </span>
          )}
        </div>
      </div>

      {/* Quick Facts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-sm border border-slate-200 p-4">
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Current Company</p>
          <p className="text-sm font-bold text-slate-900">{current_company?.name || '-'}</p>
        </div>
        <div className="bg-white rounded-sm border border-slate-200 p-4">
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Sub-Rack</p>
          <p className="text-sm font-bold text-slate-900">{sub_rack_name || '-'}</p>
        </div>
        <div className="bg-white rounded-sm border border-slate-200 p-4">
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Current Batch</p>
          <p className="text-sm font-bold font-mono text-slate-900">{batch_number || '-'}</p>
        </div>
        <div className="bg-white rounded-sm border border-slate-200 p-4">
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Linked Sale Invoice</p>
          {linked?.sale_invoice_id ? (
            <Link to={`/admin/sale/invoices/${linked.sale_invoice_id}`} data-testid="linked-sale-invoice"
              className="text-sm font-bold text-orange-600 hover:text-orange-700 inline-flex items-center gap-1">
              {linked.sale_invoice_number || 'View'} <ExternalLink className="h-3 w-3" />
            </Link>
          ) : <p className="text-sm text-slate-400">-</p>}
        </div>
      </div>

      {/* Rates from current inward */}
      {current_inward && (
        <div className="bg-white rounded-sm border border-slate-200 mb-8">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
              <BadgeDollarSign className="h-4 w-4 text-slate-500" /> Current Inward Details
            </h3>
            {current_inward.is_internal_transfer && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-purple-100 text-purple-700">
                <ArrowRightLeft className="h-3 w-3" /> via Internal Transfer
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-5">
            <div><p className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Purchase Rate</p><p className="font-bold text-slate-900">{formatINR(current_inward.purchase_rate)}</p></div>
            <div><p className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Sale Rate</p><p className="font-bold text-slate-900">{formatINR(current_inward.sale_rate)}</p></div>
            <div><p className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Display Rate</p><p className="font-bold text-orange-600">{formatINR(current_inward.display_rate)}</p></div>
            <div><p className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Inward Invoice</p><p className="font-mono text-sm text-slate-700">{current_inward.invoice_number || '-'}</p></div>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="bg-white rounded-sm border border-slate-200">
        <div className="px-5 py-3 border-b border-slate-200">
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Activity Timeline</h3>
          <p className="text-xs text-slate-500 mt-0.5">Chronological history of everything that happened to this item</p>
        </div>
        {timeline.length === 0 ? (
          <div className="text-center py-12 text-slate-400 text-sm" data-testid="timeline-empty">No activity recorded</div>
        ) : (
          <ol className="relative p-6">
            <div className="absolute left-9 top-6 bottom-6 w-px bg-slate-200" aria-hidden="true" />
            {timeline.map((evt, idx) => {
              const meta = TYPE_META[evt.type] || { icon: Package, ring: 'ring-slate-200', dot: 'bg-slate-500', label: 'Event' };
              const Icon = meta.icon;
              return (
                <li key={idx} data-testid={`timeline-event-${idx}`} className="relative pl-10 pb-6 last:pb-0">
                  <span className={`absolute left-1.5 top-0 w-7 h-7 rounded-full bg-white ring-4 ${meta.ring} flex items-center justify-center`}>
                    <span className={`block w-2.5 h-2.5 rounded-full ${meta.dot}`} />
                  </span>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="h-3.5 w-3.5 text-slate-400" />
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{meta.label}</span>
                      </div>
                      <h4 className="text-sm font-bold text-slate-900">{evt.title}</h4>
                      <p className="text-sm text-slate-600 mt-0.5">{evt.description}</p>
                      <div className="flex flex-wrap items-center gap-3 mt-2">
                        {evt.ref_label && evt.ref_link && (
                          <Link to={evt.ref_link} className="inline-flex items-center gap-1 text-xs font-medium text-orange-600 hover:text-orange-700 font-mono">
                            {evt.ref_label} <ExternalLink className="h-3 w-3" />
                          </Link>
                        )}
                        {evt.ref_label && !evt.ref_link && (
                          <span className="text-xs font-mono text-slate-500">{evt.ref_label}</span>
                        )}
                        {Array.isArray(evt.secondary) && evt.secondary.filter(Boolean).map((s, i) => (
                          <Link key={i} to={s.link} className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 font-mono">
                            {s.label} <ExternalLink className="h-3 w-3" />
                          </Link>
                        ))}
                      </div>
                    </div>
                    <span className="flex-shrink-0 text-xs text-slate-400 whitespace-nowrap">{formatDate(evt.ts)}</span>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
};
